import { removeTime } from '../../dateUtils';
import { BadInputError } from '@watershed/errors/BadInputError';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import assertNever from '@watershed/shared-util/src/assertNever';
import { ASTCall } from '@watershed/lscript/ast';
import { evalExpression } from '../eval';
import {
  stringifyType,
  DateType,
  ExprType,
} from '@watershed/lscript/expr_types';
import { exprToSql } from '../sql';
import { mergeErrors } from '../sql/internal';
import {
  TypecheckWarning,
  assertArgShape,
  assertKwargShape,
  typecheckExpression,
} from '../typecheck';
import { Context, Func, SqlError, SqlExpression } from '../types';
import isDate from 'lodash/isDate';

/**
 * Indicates whether an `ExprType` is allowed to be cast to a datetime or date.
 */
export function canBeCastToDate(exprType: ExprType): boolean {
  const { ty } = exprType;
  switch (ty) {
    case 'string':
    case 'date':
    case 'datetime':
    case 'null':
      return true;

    case 'number':
      return exprType.unit == null;

    case 'nullable':
      return canBeCastToDate(exprType.baseTy);

    case 'boolean':
    case 'list':
    case 'quantity':
      return false;

    default:
      assertNever(ty);
  }
}

// Examples:
// - 2023-06-01
// - 2023-06-01 12:10:10
// - 2023-06-01T12:10:10
// - 2023-06-01T12:10:10Z
// - 2023-06-01T12:10:10.999Z
const dateStringPattern =
  '^\\d{4}-\\d{2}-\\d{2}(?:(?:T| )?\\d{2}:\\d{2}:\\d{2}(?:\\.\\d+)?Z?)?$';

/**
 * Indicates whether a string literal matches our allowed date/datetime formats.
 */
export function isValidDateStringLiteral(s: string): boolean {
  return new RegExp(dateStringPattern).test(s);
}

// We want to rule out timestamps with second-level granularity, only accept
// milliseconds.
const dateNumberMin = new Date(300000000000).getTime();
const dateNumberMax = new Date('3000-01-01').getTime();

/**
 * Indicates whether a numeric literal matches our allowed date/datetime
 * formats.
 */
export function isValidDateNumberLiteral(n: number): boolean {
  return n >= dateNumberMin && n < dateNumberMax;
}

// The exported functions below are a bit of an awkward abstraction. But they
// are only for this one narrowly scoped case -- sharing logic between the
// `date` and `datetime` implementations. And doing something like this seems
// preferable to copy-pasting code between the two *as long as* we want every
// fix or update to apply equally to both. If that desire ever changes, and we
// need more variation between the implementations, we can always copy-paste
// this across both cases and change them there.

export function dateFunctionTypecheck<T extends 'date' | 'datetime'>(
  config: {
    functionName: 'date' | 'datetime';
    returnType: T;
  },
  ctx: Context,
  call: ASTCall,
  warnings?: Array<TypecheckWarning>
): { ty: T } {
  assertArgShape(call, 1, 1);
  assertKwargShape(call, [], false);

  const arg = call.args[0];
  const argType = typecheckExpression(ctx, arg, warnings);

  BadInputError.invariant(
    canBeCastToDate(argType),
    `\`${config.functionName}\` cannot cast an argument of type ${stringifyType(
      argType
    )}`,
    { data: { call, arg, argType } }
  );

  if (arg.t === 'string') {
    BadInputError.invariant(
      isValidDateStringLiteral(arg.v),
      `\`${config.functionName}\` cannot parse the string literal argument "${arg.v}"`,
      { data: { call, arg, argType } }
    );
  } else if (arg.t === 'number') {
    BadInputError.invariant(
      isValidDateNumberLiteral(arg.v),
      `\`${config.functionName}\` cannot parse the numeric literal argument ${arg.v}`,
      { data: { call, arg, argType } }
    );
  }

  return { ty: config.returnType };
}

export function dateFunctionEval(
  config: { functionName: 'date' | 'datetime' },
  ctx: Context,
  call: ASTCall
): Date | null {
  const arg = call.args[0];
  const value = evalExpression(ctx, arg);

  if (value === null) {
    return null;
  }

  if (isDate(value)) {
    return value;
  }

  if (typeof value === 'string') {
    BadInputError.invariant(
      isValidDateStringLiteral(value),
      `\`${config.functionName}\` cannot parse the string "${value}"`,
      { data: { call, arg, value } }
    );
    return new Date(value);
  }

  if (typeof value === 'number') {
    BadInputError.invariant(
      isValidDateNumberLiteral(value),
      `\`${config.functionName}\` cannot parse the number ${value}`,
      { data: { call, arg, value } }
    );
    return new Date(value);
  }

  throw new UnexpectedError(
    `Unexpected runtime argument to \`${
      config.functionName
    }\`: ${JSON.stringify(
      value
    )}. Expected a string, number, Date, or null, and typechecking should have prevented us from getting this far.`,
    { data: { call, value } }
  );
}

export function dateFunctionToSql(
  config: {
    functionName: 'date' | 'datetime';
    duckdbType: 'DATE' | 'TIMESTAMP';
  },
  ctx: Context,
  call: ASTCall
): SqlExpression {
  const arg = call.args[0];
  const argType = typecheckExpression(ctx, arg);
  const eSql = exprToSql(ctx, call.args[0]);

  const ty = argType.ty === 'nullable' ? argType.baseTy.ty : argType.ty;

  let expr: string;
  let sqlError: SqlError | null;
  switch (ty) {
    case 'date':
    case 'datetime':
    case 'null':
      expr = `try_cast(${eSql.expr} AS ${config.duckdbType})`;
      sqlError = null;
      break;

    case 'string':
      expr = `try_cast(${eSql.expr} AS ${config.duckdbType})`;
      sqlError = {
        exists: `regexp_matches(${eSql.expr}, '${dateStringPattern}') IS FALSE`,
        expr: `'\`${config.functionName}\` cannot parse the string "' || ${eSql.expr} || '"'`,
      };
      break;

    case 'number':
      expr = `try_cast(epoch_ms(CAST(${eSql.expr} AS BIGINT)) AS ${config.duckdbType})`;
      sqlError = {
        exists: `${eSql.expr} < ${dateNumberMin} OR ${eSql.expr} > ${dateNumberMax}`,
        expr: `'\`${config.functionName}\` cannot parse the number ' || CAST(${eSql.expr} AS STRING) || ''`,
      };
      break;

    case 'boolean':
    case 'list':
    case 'quantity':
      throw new UnexpectedError(
        `\`${
          config.functionName
        }\` cannot cast an argument of type ${stringifyType(
          argType
        )}: this should have already been caught by typechecking prior to evaluation!`
      );

    default:
      assertNever(ty);
  }

  return {
    expr,
    stage: eSql.stage,
    unitExpr: null,
    error: mergeErrors([eSql.error, sqlError]),
    trace: null,
  };
}

export const date: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): DateType {
    return dateFunctionTypecheck(
      { functionName: 'date', returnType: 'date' },
      ctx,
      call,
      warnings
    );
  },

  eval(ctx: Context, call: ASTCall): Date | null {
    const baseResult = dateFunctionEval({ functionName: 'date' }, ctx, call);
    if (baseResult === null) {
      return baseResult;
    }
    return removeTime(baseResult);
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    return dateFunctionToSql(
      { functionName: 'date', duckdbType: 'DATE' },
      ctx,
      call
    );
  },
};
